import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import API from "../../../api/api";
import { resolveAssetUrl } from "../../../api/config";
import {
  FaClipboardCheck,
  FaSave,
  FaPrint,
  FaHistory,
  FaFileAlt,
  FaCheckCircle,
  FaPaperclip,
  FaTimes,
  FaUser,
  FaDownload,
  FaTrashAlt,
  FaFilePdf,
} from "react-icons/fa";
import {
  printHtmlViaIframe,
  getUnifiedPrintHtml,
  downloadHtmlAsPdf,
  downloadDomPagesAsPdf,
} from "../../../utils/pdfDownloadHelper";
import Swal from "sweetalert2";

const CoachReportCards = () => {
  const [athletes, setAthletes] = useState([]);
  const [selectedAthleteId, setSelectedAthleteId] = useState("");
  const [selectedWeek, setSelectedWeek] = useState(1);
  const [saving, setSaving] = useState(false);

  // Form State - Separated Skill & Technique
  const [endurance, setEndurance] = useState("Good");
  const [strength, setStrength] = useState("Good");
  const [agility, setAgility] = useState("Good");
  const [skill, setSkill] = useState("Good");
  const [technique, setTechnique] = useState("Good");
  const [discipline, setDiscipline] = useState("Good");
  const [performanceRating, setPerformanceRating] = useState("Good");
  const [remarks, setRemarks] = useState("");

  // File Upload State
  const [selectedFile, setSelectedFile] = useState(null);
  const [currentWeekFilePath, setCurrentWeekFilePath] = useState(null);
  const fileInputRef = useRef(null);

  // Historical records for the currently selected athlete
  const [reportHistory, setReportHistory] = useState([]);
  const [signatures, setSignatures] = useState(null);

  // Bulk data for printing all athletes
  const [allAthletesData, setAllAthletesData] = useState([]);

  // Print scope: "all_weeks" (all 9 weeks, 3 weeks/page) vs "active_weeks" (recorded weeks only)
  const [printScope, setPrintScope] = useState("all_weeks");

  // Print mode: "weekly_cards" vs "individual_athlete"
  const [printMode, setPrintMode] = useState("weekly_cards");
  const [isBlankTemplate, setIsBlankTemplate] = useState(false);

  // Form Locking state (Point 14)
  const [isWeekLocked, setIsWeekLocked] = useState(false);

  // Individual Athlete Report Modal & Filters (Point 17)
  const [individualModalOpen, setIndividualModalOpen] = useState(false);
  const [individualScope, setIndividualScope] = useState("all"); // "all" | "team" | "single"
  const [selectedIndividualAthleteId, setSelectedIndividualAthleteId] = useState("");
  const [reportSelectedTeam, setReportSelectedTeam] = useState("all");
  const [reportDayMode, setReportDayMode] = useState("single_day"); // "all_days" | "single_day" (defaults to single_day)
  const [reportSingleDayNum, setReportSingleDayNum] = useState(1);
  const [reportSingleDate, setReportSingleDate] = useState(() => {
    const d = new Date();
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  });
  const [activeCamp, setActiveCamp] = useState(null);

  // Get current logged-in coach info
  const loggedInCoach = (() => {
    try {
      return (
        JSON.parse(localStorage.getItem("coach_user")) ||
        JSON.parse(localStorage.getItem("user")) ||
        {}
      );
    } catch {
      return {};
    }
  })();

  // Fetch athletes list and bulk report cards on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        const athletesRes = await API.camp.coachPortal.getMyAthletes();
        const list = athletesRes?.data?.athletes || athletesRes?.athletes || [];
        setAthletes(list);

        // Preload active camp
        try {
          const campRes = await API.camp.coachPortal.getActiveCamp();
          const cData = campRes?.data?.camp || campRes?.camp || athletesRes?.data?.camp || null;
          if (cData) setActiveCamp(cData);
        } catch (campErr) {
          console.warn("Could not fetch active camp:", campErr);
        }

        if (list.length > 0) {
          setSelectedAthleteId(String(list[0].id));
        }

        // Preload bulk data for all athletes
        try {
          const bulkRes = await API.camp.coachPortal.getAllAthletesReportCards();
          const bulkList = bulkRes?.data?.data || bulkRes?.data || [];
          setAllAthletesData(bulkList);
          if (bulkRes?.data?.signatures) {
            setSignatures(bulkRes.data.signatures);
          }
        } catch (bulkErr) {
          console.warn("Could not prefetch bulk report cards:", bulkErr);
        }
      } catch (err) {
        console.error("Failed to load athletes:", err);
      }
    };

    fetchData();
  }, []);

  // Fetch athlete's report card history whenever selected athlete changes
  useEffect(() => {
    if (!selectedAthleteId || selectedAthleteId === "all") {
      setReportHistory([]);
      return;
    }

    const fetchHistory = async () => {
      try {
        const res = await API.camp.coachPortal.getAthleteReportCard(selectedAthleteId);
        const data = res?.data || res;
        const weeks = data?.weeks || [];
        setReportHistory(weeks);
        if (data?.signatures) {
          setSignatures(data.signatures);
        }

        // Populate form for the current selected week
        populateWeekForm(weeks, selectedWeek);
      } catch (err) {
        console.error("Error loading athlete report cards:", err);
      }
    };

    fetchHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAthleteId]);

  // When selected week changes, update form values from historical records
  useEffect(() => {
    if (selectedAthleteId && selectedAthleteId !== "all") {
      populateWeekForm(reportHistory, selectedWeek);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedWeek]);

  const populateWeekForm = (history, weekNum) => {
    const card = (history || []).find((w) => w.week_number === parseInt(weekNum));
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }

    if (card) {
      setIsWeekLocked(Boolean(card.is_locked));
      setEndurance(card.endurance || "Good");
      setStrength(card.strength || "Good");
      setAgility(card.agility || "Good");
      setSkill(
        card.skill ||
          (card.skill_technique ? card.skill_technique.split("/")[0]?.trim() : "Good")
      );
      setTechnique(
        card.technique ||
          (card.skill_technique
            ? card.skill_technique.split("/")[1]?.trim() || "Good"
            : "Good")
      );
      setDiscipline(card.discipline || "Good");
      setPerformanceRating(card.performance_rating || "Good");
      setRemarks(card.remarks || "");
      setCurrentWeekFilePath(card.file_path || null);
    } else {
      setIsWeekLocked(false);
      setEndurance("Good");
      setStrength("Good");
      setAgility("Good");
      setSkill("Good");
      setTechnique("Good");
      setDiscipline("Good");
      setPerformanceRating("Good");
      setRemarks("");
      setCurrentWeekFilePath(null);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) {
      setSelectedFile(null);
      return;
    }

    // Limit 10MB
    if (file.size > 10 * 1024 * 1024) {
      Swal.fire("File Too Large", "Maximum allowed upload size is 10MB.", "warning");
      e.target.value = "";
      setSelectedFile(null);
      return;
    }

    setSelectedFile(file);
  };

  const handleSave = async (e, isFinalSubmit = false) => {
    if (e && e.preventDefault) e.preventDefault();
    if (isWeekLocked) {
      Swal.fire("Locked", "This week's evaluation has been finalized and locked. Contact Admin/Secretary for modifications.", "warning");
      return;
    }

    if (!selectedAthleteId || selectedAthleteId === "all") {
      Swal.fire("Select Athlete", "Please select an individual athlete to record their weekly report card.", "info");
      return;
    }

    if (isFinalSubmit) {
      const confirm = await Swal.fire({
        title: "Finalize & Submit Evaluation?",
        text: `Are you sure you want to finalize and submit Week ${selectedWeek} evaluation for ${selectedAthlete?.name}? Once submitted, this evaluation will be locked.`,
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Yes, Submit & Lock",
        confirmButtonColor: "#0e7490",
        cancelButtonText: "Cancel",
      });
      if (!confirm.isConfirmed) return;
    }

    setSaving(true);
    try {
      let campId = activeCamp?.id;
      if (!campId) {
        const activeCampRes = await API.camp.coachPortal.getActiveCamp();
        campId = activeCampRes?.data?.camp?.id || activeCampRes?.camp?.id || 1;
        if (activeCampRes?.data?.camp) setActiveCamp(activeCampRes.data.camp);
      }

      const formData = new FormData();
      formData.append("camp_id", campId);
      formData.append("athlete_id", selectedAthleteId);
      formData.append("week_number", selectedWeek);
      formData.append("endurance", endurance);
      formData.append("strength", strength);
      formData.append("agility", agility);
      formData.append("skill", skill);
      formData.append("technique", technique);
      formData.append("skill_technique", `${skill} / ${technique}`);
      formData.append("discipline", discipline);
      formData.append("performance_rating", performanceRating);
      formData.append("remarks", remarks || "");
      formData.append("is_locked", isFinalSubmit ? "1" : "0");

      // Only attach file on Final Submit to prevent accidental wrong file saves during drafting
      if (isFinalSubmit && selectedFile) {
        formData.append("file", selectedFile);
      }

      const res = await API.camp.coachPortal.saveReportCard(formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (res?.data?.success || res?.success) {
        const successMessage = isFinalSubmit
          ? `Week ${selectedWeek} Report Card submitted and finalized successfully!`
          : selectedFile
            ? `Week ${selectedWeek} draft saved. Staged document '${selectedFile.name}' will be uploaded upon Final Submit.`
            : `Week ${selectedWeek} report card progress saved as draft.`;

        Swal.fire(
          isFinalSubmit ? "Submitted & Locked" : "Saved",
          successMessage,
          "success"
        );

        // Refetch single athlete history
        const refreshed = await API.camp.coachPortal.getAthleteReportCard(selectedAthleteId);
        const updatedWeeks = refreshed?.data?.weeks || refreshed?.weeks || [];
        setReportHistory(updatedWeeks);
        populateWeekForm(updatedWeeks, selectedWeek);

        // Also refresh bulk data in background
        API.camp.coachPortal.getAllAthletesReportCards().then((bRes) => {
          setAllAthletesData(bRes?.data?.data || bRes?.data || []);
        }).catch(() => {});
      }
    } catch (err) {
      Swal.fire("Error", err?.response?.data?.message || err?.message || "Failed to save report card", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteFile = async () => {
    if (isWeekLocked) {
      Swal.fire("Locked", "This week's evaluation has been finalized and locked. Contact Admin/Secretary for modifications.", "warning");
      return;
    }

    const confirm = await Swal.fire({
      title: "Delete Uploaded Document?",
      text: `Are you sure you want to delete the attached document for Week ${selectedWeek}?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, Delete",
      confirmButtonColor: "#dc2626",
      cancelButtonText: "Cancel",
    });
    if (!confirm.isConfirmed) return;

    try {
      let campId = activeCamp?.id;
      if (!campId) {
        const activeCampRes = await API.camp.coachPortal.getActiveCamp();
        campId = activeCampRes?.data?.camp?.id || activeCampRes?.camp?.id || 1;
      }
      const res = await API.camp.coachPortal.deleteReportCardFile({
        camp_id: campId,
        athlete_id: selectedAthleteId,
        week_number: selectedWeek,
      });

      if (res?.data?.success || res?.success) {
        setCurrentWeekFilePath(null);
        setSelectedFile(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
        Swal.fire("Deleted", "Uploaded document removed successfully.", "success");

        const refreshed = await API.camp.coachPortal.getAthleteReportCard(selectedAthleteId);
        const updatedWeeks = refreshed?.data?.weeks || refreshed?.weeks || [];
        setReportHistory(updatedWeeks);
      } else {
        Swal.fire("Error", res?.data?.message || "Failed to delete document", "error");
      }
    } catch (err) {
      Swal.fire("Error", err?.response?.data?.message || err?.message || "Failed to delete document", "error");
    }
  };

  const handlePrintWeeklyCards = async (isBlank = false) => {
    setIsBlankTemplate(isBlank);
    setPrintMode("weekly_cards");
    if (selectedAthleteId === "all" || !selectedAthleteId) {
      try {
        const bulkRes = await API.camp.coachPortal.getAllAthletesReportCards();
        setAllAthletesData(bulkRes?.data?.data || bulkRes?.data || []);
      } catch (e) {
        console.error("Failed to load bulk data for printing:", e);
      }
    }
    setTimeout(() => {
      const container = document.querySelector(".print-container");
      if (container) {
        const printHtml = getUnifiedPrintHtml(
          isBlank ? "Blank Athlete Report Cards" : "Athlete Weekly Report Cards",
          container.innerHTML,
          { orientation: "portrait" }
        );
        printHtmlViaIframe(printHtml);
      }
    }, 350);
  };

  const handleDownloadWeeklyCards = async (isBlank = false) => {
    setIsBlankTemplate(isBlank);
    setPrintMode("weekly_cards");
    if (selectedAthleteId === "all" || !selectedAthleteId) {
      try {
        const bulkRes = await API.camp.coachPortal.getAllAthletesReportCards();
        setAllAthletesData(bulkRes?.data?.data || bulkRes?.data || []);
      } catch (error) {
        console.error("Failed to load bulk data for PDF:", error);
      }
    }
    setTimeout(() => downloadDomPagesAsPdf(
      ".print-container",
      isBlank ? "Blank_Athlete_Report_Cards" : "Athlete_Weekly_Report_Cards",
      { orientation: "portrait", pageSelector: ".print-report-page" }
    ), 350);
  };
  // Helper to resolve team / squad name for an athlete
  const getTeamNameForAthlete = useCallback((ath) => {
    if (ath?.mappings && ath.mappings.length > 0) {
      if (ath.mappings[0]?.team_name) return ath.mappings[0].team_name;
      if (ath.mappings[0]?.coach?.team_name) return ath.mappings[0].coach.team_name;
    }
    if (ath?.team_name) {
      return ath.team_name;
    }
    if (ath?.coach?.team_name) {
      return ath.coach.team_name;
    }
    return loggedInCoach?.team_name || "General Squad";
  }, [loggedInCoach]);

  const distinctTeams = useMemo(() => {
    const set = new Set();
    athletes.forEach((a) => {
      const t = getTeamNameForAthlete(a);
      if (t && typeof t === "string" && t.trim()) set.add(t.trim());
    });
    return Array.from(set).sort();
  }, [athletes, getTeamNameForAthlete]);

  const getCampDaysList = useCallback((athlete) => {
    const camp = activeCamp || athlete?.camp || athletes[0]?.camp;
    const startDateStr = camp?.start_date;
    const endDateStr = camp?.end_date;

    if (startDateStr && endDateStr) {
      const start = new Date(startDateStr);
      const end = new Date(endDateStr);
      if (!isNaN(start.getTime()) && !isNaN(end.getTime()) && end >= start) {
        const days = [];
        let cur = new Date(start);
        let dayNum = 1;
        while (cur <= end) {
          const yyyy = cur.getFullYear();
          const mm = String(cur.getMonth() + 1).padStart(2, "0");
          const dd = String(cur.getDate()).padStart(2, "0");
          days.push({
            dayNumber: dayNum,
            dateStr: `${dd}-${mm}-${yyyy}`,
            isoDate: `${yyyy}-${mm}-${dd}`,
            label: `Day ${dayNum} (${dd}-${mm}-${yyyy})`,
          });
          cur.setDate(cur.getDate() + 1);
          dayNum++;
        }
        if (days.length > 0) return days;
      }
    }

    // Fallback if camp dates not set in database
    const now = new Date();
    const dd = String(now.getDate()).padStart(2, "0");
    const mm = String(now.getMonth() + 1).padStart(2, "0");
    const yyyy = now.getFullYear();
    return [{
      dayNumber: 1,
      dateStr: `${dd}-${mm}-${yyyy}`,
      isoDate: `${yyyy}-${mm}-${dd}`,
      label: `Day 1 (${dd}-${mm}-${yyyy})`,
    }];
  }, [athletes, activeCamp]);

  const handleConfirmIndividualAthleteAction = async (action = "print") => {
    setIndividualModalOpen(false);

    setPrintMode("individual_athlete");
    if (athletes.length === 0) {
      try {
        const athletesRes = await API.camp.coachPortal.getMyAthletes();
        const list = athletesRes?.data?.athletes || athletesRes?.athletes || [];
        setAthletes(list);
      } catch (e) {
        console.error("Failed to load athletes for report output:", e);
      }
    }

    const campName = activeCamp?.name || "Coaching_Camp";
    const dayLabel = reportDayMode === "all_days" ? "All_Days" : `Day_${selectedReportDay?.dayNumber || reportSingleDayNum || 1}`;
    const scopeLabel = individualScope === "single" ? "Single_Athlete" : (individualScope === "team" ? (reportSelectedTeam || "Team") : `All_${athletes.length || "Assigned"}_Athletes`);
    const cleanTitle = `Individual_Athlete_Daily_Evaluation_Reports_${campName}_${dayLabel}_${scopeLabel}`.replace(/[^a-zA-Z0-9_-]/g, "_");

    setTimeout(async () => {
      const container = document.querySelector(".print-container");
      if (!container) return;

      const reportHtml = getUnifiedPrintHtml(
        "Individual Athlete Daily Evaluation Reports",
        container.innerHTML,
        { orientation: "portrait" }
      );

      if (action === "print") {
        printHtmlViaIframe(reportHtml);
      } else {
        await downloadHtmlAsPdf(reportHtml, cleanTitle, { orientation: "portrait" });
      }
      setTimeout(() => setPrintMode(null), 1000);
    }, 350);
  };

  const ratingOptions = ["Excellent", "Good", "Average", "Poor"];

  const selectedAthlete = athletes.find((a) => String(a.id) === String(selectedAthleteId));
  const selectedReportCampDays = useMemo(() => {
    return getCampDaysList(selectedAthlete || athletes[0]);
  }, [getCampDaysList, selectedAthlete, athletes]);

  // Synchronize selected day with current selected date and day number
  const selectedReportDay = useMemo(() => {
    if (reportSingleDate) {
      const byDate = selectedReportCampDays.find((day) => day.isoDate === reportSingleDate);
      if (byDate) return byDate;

      // Dynamically calculate day number if selected date is outside standard camp list (e.g. future date)
      const camp = activeCamp || selectedAthlete?.camp || athletes[0]?.camp;
      const campStart = camp?.start_date ? new Date(camp.start_date + "T00:00:00") : new Date("2026-08-01T00:00:00");
      const chosen = new Date(reportSingleDate + "T00:00:00");
      let calcDayNum = reportSingleDayNum || 1;
      if (!isNaN(chosen.getTime()) && !isNaN(campStart.getTime())) {
        const diff = Math.round((chosen - campStart) / 86400000) + 1;
        if (diff > 0) calcDayNum = diff;
      }
      const [yyyy, mm, dd] = reportSingleDate.split("-");
      return {
        dayNumber: calcDayNum,
        dateStr: `${dd}-${mm}-${yyyy}`,
        isoDate: reportSingleDate,
        label: `Day ${calcDayNum} (${dd}-${mm}-${yyyy})`,
      };
    }

    const byNum = selectedReportCampDays.find((day) => day.dayNumber === reportSingleDayNum);
    if (byNum) return byNum;

    return {
      dayNumber: reportSingleDayNum || 1,
      dateStr: "22-09-2026",
      isoDate: "2026-09-22",
      label: `Day ${reportSingleDayNum || 1} (22-09-2026)`,
    };
  }, [selectedReportCampDays, reportSingleDate, reportSingleDayNum, activeCamp, selectedAthlete, athletes]);

  // Auto-match today's date within camp days on mount once
  const reportDateInitRef = useRef(false);
  useEffect(() => {
    if (!reportDateInitRef.current && selectedReportCampDays && selectedReportCampDays.length > 0) {
      const todayIso = new Date().toISOString().slice(0, 10);
      const match = selectedReportCampDays.find((d) => d.isoDate === todayIso);
      if (match) {
        setReportSingleDayNum(match.dayNumber);
        setReportSingleDate(match.isoDate);
      } else {
        const camp = activeCamp || selectedAthlete?.camp || athletes[0]?.camp;
        const campStart = camp?.start_date ? new Date(camp.start_date + "T00:00:00") : new Date("2026-08-01T00:00:00");
        const todayDate = new Date(todayIso + "T00:00:00");
        const diff = Math.round((todayDate - campStart) / 86400000) + 1;
        setReportSingleDayNum(diff > 0 ? diff : 1);
        setReportSingleDate(todayIso);
      }
      reportDateInitRef.current = true;
    }
  }, [selectedReportCampDays, activeCamp, selectedAthlete, athletes]);

  // Helper to resolve assistant coach name for an athlete
  const getCoachNameForAthlete = (ath) => {
    if (ath?.mappings && ath.mappings.length > 0 && ath.mappings[0]?.coach?.name) {
      return ath.mappings[0].coach.name;
    }
    if (ath?.coach?.name) {
      return ath.coach.name;
    }
    return loggedInCoach?.name || "Designated Camp Assistant Coach";
  };

  // Helper to chunk an array into groups of max `size`
  const chunkArray = (arr, size = 3) => {
    const chunks = [];
    for (let i = 0; i < arr.length; i += size) {
      chunks.push(arr.slice(i, i + size));
    }
    return chunks;
  };

  // Build the array of weeks to display for printing
  const getPrintWeeksForAthlete = (athleteWeeks) => {
    if (isBlankTemplate) {
      return [1, 2, 3, 4, 5, 6, 7, 8, 9].map((wNum) => ({
        week_number: wNum,
        endurance: "",
        strength: "",
        agility: "",
        skill: "",
        technique: "",
        discipline: "",
        performance_rating: "",
        remarks: "",
      }));
    }

    if (printScope === "all_weeks") {
      return [1, 2, 3, 4, 5, 6, 7, 8, 9].map((wNum) => {
        const existing = (athleteWeeks || []).find((w) => w.week_number === wNum);
        return (
          existing || {
            week_number: wNum,
            endurance: "-",
            strength: "-",
            agility: "-",
            skill: "-",
            technique: "-",
            discipline: "-",
            performance_rating: "-",
            remarks: "Not Evaluated",
          }
        );
      });
    } else {
      // active_weeks: only weeks that have records (or default 1-3 if none)
      if (!athleteWeeks || athleteWeeks.length === 0) {
        return [1, 2, 3].map((wNum) => ({
          week_number: wNum,
          endurance: "-",
          strength: "-",
          agility: "-",
          skill: "-",
          technique: "-",
          discipline: "-",
          performance_rating: "-",
          remarks: "No Evaluation Recorded",
        }));
      }
      return [...athleteWeeks].sort((a, b) => a.week_number - b.week_number);
    }
  };

  // Build the list of athletes to render in the print area
  const athletesToPrint = (() => {
    if (selectedAthleteId && selectedAthleteId !== "all") {
      if (!selectedAthlete) return [];
      return [
        {
          athlete: selectedAthlete,
          weeks: reportHistory,
        },
      ];
    }

    // All athletes
    if (allAthletesData && allAthletesData.length > 0) {
      return allAthletesData;
    }

    // Fallback: map athletes list with empty weeks
    return athletes.map((ath) => ({
      athlete: ath,
      weeks: [],
    }));
  })();

  return (
    <div className="space-y-6">
      {/* =========================================================
          PRINT-ONLY CSS STYLES
      ========================================================= */}
      <style dangerouslySetInnerHTML={{
        __html: `
          @media print {
            body {
              background-color: #ffffff !important;
              color: #000000 !important;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
            .print\\:hidden {
              display: none !important;
            }
            .print\\:block {
              display: block !important;
            }
            @page {
              size: A4 portrait;
              margin: 8mm 10mm 8mm 10mm;
            }
            .print-report-page {
              page-break-after: always !important;
              break-after: page !important;
              page-break-inside: avoid !important;
              break-inside: avoid !important;
              box-sizing: border-box;
              margin: 0;
              padding: 0;
              display: flex !important;
              flex-direction: column !important;
              justify-content: space-between !important;
            }
            .print-report-page:last-child {
              page-break-after: auto !important;
              break-after: auto !important;
            }
            .individual-student-page {
              page-break-after: always !important;
              break-after: page !important;
              page-break-inside: avoid !important;
              break-inside: avoid !important;
              height: 275mm;
              max-height: 275mm;
              box-sizing: border-box;
              margin: 0;
              padding: 0;
              display: flex !important;
              flex-direction: column !important;
              justify-content: space-between !important;
            }
            .individual-student-page:last-child {
              page-break-after: auto !important;
              break-after: auto !important;
            }
          }
        `,
      }} />

      {/* =========================================================
          INDIVIDUAL ATHLETE REPORT CONFIGURATION MODAL
      ========================================================= */}
      {individualModalOpen && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-3 sm:p-4 print:hidden">
          <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-lg overflow-hidden">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-emerald-800 via-teal-800 to-cyan-900 p-5 text-white flex items-center justify-between">
              <div>
                <span className="text-[11px] font-bold uppercase tracking-widest text-emerald-200">Official Daily Evaluation</span>
                <h3 className="text-lg sm:text-xl font-black tracking-tight text-white flex items-center gap-2 mt-0.5">
                  <FaFileAlt /> Individual Athlete Report
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setIndividualModalOpen(false)}
                className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white text-lg transition-colors"
              >
                &times;
              </button>
            </div>

            <div className="p-5 space-y-4 text-xs text-slate-700">
              {/* Generation Scope (Point 17) */}
              <div>
                <label className="block font-bold text-slate-800 uppercase tracking-wider text-[11px] mb-1.5">
                  Report Generation Scope *
                </label>
                <div className="grid grid-cols-3 gap-2 mb-3">
                  <button
                    type="button"
                    onClick={() => setIndividualScope("all")}
                    className={`p-2.5 rounded-xl border text-xs font-bold text-center transition ${
                      individualScope === "all"
                        ? "bg-emerald-600 text-white border-emerald-600 shadow-sm"
                        : "bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100"
                    }`}
                  >
                    All Athletes ({athletes.length})
                  </button>
                  <button
                    type="button"
                    onClick={() => setIndividualScope("team")}
                    className={`p-2.5 rounded-xl border text-xs font-bold text-center transition ${
                      individualScope === "team"
                        ? "bg-emerald-600 text-white border-emerald-600 shadow-sm"
                        : "bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100"
                    }`}
                  >
                    By Team / Squad
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setIndividualScope("single");
                      if (!selectedIndividualAthleteId && athletes.length > 0) {
                        setSelectedIndividualAthleteId(String(athletes[0].id));
                      }
                    }}
                    className={`p-2.5 rounded-xl border text-xs font-bold text-center transition ${
                      individualScope === "single"
                        ? "bg-emerald-600 text-white border-emerald-600 shadow-sm"
                        : "bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100"
                    }`}
                  >
                    Single Athlete
                  </button>
                </div>

                {individualScope === "team" && (
                  <div className="mb-2">
                    <label className="block font-bold text-slate-700 uppercase tracking-wider text-[10.5px] mb-1">
                      Select Team *
                    </label>
                    <select
                      value={reportSelectedTeam}
                      onChange={(e) => setReportSelectedTeam(e.target.value)}
                      className="w-full bg-slate-50 border border-slate-300 rounded-xl px-3 py-2 text-slate-900 text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    >
                      <option value="all">All Teams</option>
                      {distinctTeams.map((team) => (
                        <option key={team} value={team}>{team}</option>
                      ))}
                    </select>
                  </div>
                )}

                {individualScope === "single" && (
                  <div className="mb-2">
                    <label className="block font-bold text-slate-700 uppercase tracking-wider text-[10.5px] mb-1">
                      Select Athlete *
                    </label>
                    <select
                      value={selectedIndividualAthleteId}
                      onChange={(e) => setSelectedIndividualAthleteId(e.target.value)}
                      className="w-full bg-slate-50 border border-slate-300 rounded-xl px-3 py-2 text-slate-900 text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    >
                      {athletes.map((ath) => (
                        <option key={ath.id} value={ath.id}>
                          {ath.name} (BIB: {ath.bib_no || "N/A"}) - {ath.district || "N/A"}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>

              {/* Day / Date Mode */}
              <div>
                <label className="block font-bold text-slate-800 uppercase tracking-wider text-[11px] mb-1.5">
                  Camp Daily Evaluation (Days) *
                </label>
                <div className="space-y-2">
                  <label className="flex items-start gap-2.5 p-3 rounded-xl border border-slate-200 hover:bg-slate-50 cursor-pointer transition">
                    <input
                      type="radio"
                      name="dayMode"
                      value="all_days"
                      checked={reportDayMode === "all_days"}
                      onChange={() => setReportDayMode("all_days")}
                      className="mt-0.5 text-emerald-600 focus:ring-emerald-500"
                    />
                    <div>
                      <div className="font-bold text-slate-900">Each and Every Day of Camp (All Days)</div>
                      <p className="text-[10.5px] text-slate-500">
                        Generates a separate daily training evaluation page for every athlete for each day of the camp (Day 1 to Day N, 1 page per day).
                      </p>
                    </div>
                  </label>

                  <label className="flex items-start gap-2.5 p-3 rounded-xl border border-slate-200 hover:bg-slate-50 cursor-pointer transition">
                    <input
                      type="radio"
                      name="dayMode"
                      value="single_day"
                      checked={reportDayMode === "single_day"}
                      onChange={() => setReportDayMode("single_day")}
                      className="mt-0.5 text-emerald-600 focus:ring-emerald-500"
                    />
                    <div className="flex-1">
                      <div className="font-bold text-slate-900">Specific Day / Single Date</div>
                      <p className="text-[10.5px] text-slate-500">
                        Generates 1 page per athlete for the chosen single camp day.
                      </p>
                    </div>
                  </label>
                </div>
              </div>

              {/* If Single Day selected, show Day Number & Date Inputs */}
              {reportDayMode === "single_day" && (
                <div className="grid grid-cols-2 gap-3 p-3 bg-slate-50 rounded-xl border border-slate-200">
                  <div>
                    <label className="block font-bold text-slate-700 text-[10px] uppercase mb-1">Camp Day Number</label>
                    <select
                      value={reportSingleDayNum}
                      onChange={(e) => {
                        const dayNumber = Number(e.target.value);
                        setReportSingleDayNum(dayNumber);
                        const matchDay = selectedReportCampDays.find((d) => d.dayNumber === dayNumber);
                        if (matchDay?.isoDate) {
                          setReportSingleDate(matchDay.isoDate);
                        }
                      }}
                      className="w-full bg-white border border-slate-300 rounded-lg px-2.5 py-1.5 text-xs font-semibold focus:ring-2 focus:ring-emerald-500"
                    >
                      {selectedReportCampDays.map((day) => (
                        <option key={day.dayNumber} value={day.dayNumber}>{day.label}</option>
                      ))}
                      {!selectedReportCampDays.some((d) => d.dayNumber === reportSingleDayNum) && (
                        <option value={reportSingleDayNum}>
                          Day {reportSingleDayNum} ({selectedReportDay?.dateStr || reportSingleDate})
                        </option>
                      )}
                    </select>
                  </div>
                  <div>
                    <label className="block font-bold text-slate-700 text-[10px] uppercase mb-1">Date</label>
                    <input
                      type="date"
                      value={reportSingleDate}
                      onChange={(e) => {
                        const newDate = e.target.value;
                        if (!newDate) return;
                        setReportSingleDate(newDate);
                        const matchDay = selectedReportCampDays.find((d) => d.isoDate === newDate);
                        if (matchDay) {
                          setReportSingleDayNum(matchDay.dayNumber);
                        } else {
                          const camp = activeCamp || selectedAthlete?.camp || athletes[0]?.camp;
                          const campStart = camp?.start_date ? new Date(camp.start_date + "T00:00:00") : new Date("2026-08-01T00:00:00");
                          const chosen = new Date(newDate + "T00:00:00");
                          if (!isNaN(chosen.getTime()) && !isNaN(campStart.getTime())) {
                            const diff = Math.round((chosen - campStart) / 86400000) + 1;
                            setReportSingleDayNum(diff > 0 ? diff : 1);
                          }
                        }
                      }}
                      className="w-full bg-white border border-slate-300 rounded-lg px-2.5 py-1.5 text-xs font-semibold text-slate-800 focus:ring-2 focus:ring-emerald-500"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Modal Actions */}
            <div className="p-4 bg-slate-50 border-t border-slate-200 flex flex-wrap items-center justify-end gap-2.5">
              <button
                type="button"
                onClick={() => setIndividualModalOpen(false)}
                className="px-4 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-200 rounded-xl transition"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => handleConfirmIndividualAthleteAction("print")}
                className="px-4 py-2.5 text-xs font-bold text-white bg-slate-800 hover:bg-slate-900 rounded-xl shadow flex items-center gap-1.5 transition cursor-pointer"
              >
                <FaPrint /> Print Reports
              </button>
              <button
                type="button"
                onClick={() => handleConfirmIndividualAthleteAction("download")}
                className="px-4 py-2.5 text-xs font-bold text-white bg-emerald-700 hover:bg-emerald-800 rounded-xl shadow flex items-center gap-1.5 transition cursor-pointer"
              >
                <FaDownload /> Download PDF (Manager)
              </button>
            </div>
          </div>
        </div>
      )}

      {/* =========================================================
          SCREEN UI: HEADER & CONTROLS (Hidden when printing)
      ========================================================= */}
      <section className="rounded-2xl border border-amber-200 bg-gradient-to-br from-amber-50 via-white to-cyan-50 p-4 sm:p-5 shadow-sm print:hidden">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-cyan-700">Performance Assessment</p>
            <h1 className="text-2xl sm:text-3xl font-semibold text-slate-900 mt-0.5 tracking-tight flex items-center gap-2">
              <FaClipboardCheck className="text-cyan-700" /> Athlete Weekly Report Cards
            </h1>
            <p className="text-xs text-slate-600 mt-1">
              Weekly evaluation across Endurance, Strength, Agility, Skill, Technique, and Discipline with document upload and printable report card generation.
            </p>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <select
              value={printScope}
              onChange={(e) => setPrintScope(e.target.value)}
              className="bg-white border border-slate-300 text-slate-800 text-xs rounded-xl px-3 py-2 font-medium shadow-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
              title="Select print format"
            >
              <option value="all_weeks">Weekly Cards: All 9 Weeks</option>
              <option value="active_weeks">Weekly Cards: Recorded Only</option>
            </select>

            {/* Blank Template (Print & Download) */}
            <div className="inline-flex rounded-xl shadow-sm border border-slate-300 overflow-hidden bg-white">
              <button
                type="button"
                onClick={() => handlePrintWeeklyCards(true)}
                className="px-3 py-2 bg-slate-100 hover:bg-slate-200 text-slate-800 text-xs font-semibold flex items-center gap-1.5 border-r border-slate-200 transition"
                title="Print blank weekly assessment template"
              >
                <FaPrint className="text-cyan-700" /> Print Blank
              </button>
              <button
                type="button"
                onClick={() => handleDownloadWeeklyCards(true)}
                className="px-3 py-2 bg-slate-100 hover:bg-slate-200 text-slate-800 text-xs font-semibold flex items-center gap-1.5 transition"
                title="Generate and download blank weekly assessment PDF"
              >
                <FaDownload className="text-cyan-700" /> Download Blank
              </button>
            </div>

            {/* Entered Data (Print & Download) */}
            <div className="inline-flex rounded-xl shadow-sm border border-cyan-800 overflow-hidden">
              <button
                type="button"
                onClick={() => handlePrintWeeklyCards(false)}
                className="px-3 py-2 bg-cyan-700 hover:bg-cyan-800 text-white text-xs font-semibold flex items-center gap-1.5 border-r border-cyan-800/60 transition"
                title="Print populated weekly assessment report cards"
              >
                <FaPrint /> Print Entered
              </button>
              <button
                type="button"
                onClick={() => handleDownloadWeeklyCards(false)}
                className="px-3 py-2 bg-cyan-700 hover:bg-cyan-800 text-white text-xs font-semibold flex items-center gap-1.5 transition"
                title="Generate and download populated weekly assessment PDF"
              >
                <FaDownload /> Download PDF
              </button>
            </div>

            {/* Individual Athlete Report */}
            <button
              type="button"
              onClick={() => setIndividualModalOpen(true)}
              className="px-3.5 py-2 bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl text-xs font-bold flex items-center gap-1.5 transition shadow"
              title="Official individual athlete daily training evaluation report (Print or Download)"
            >
              <FaFileAlt /> Individual Athlete Report (Print / Download)
            </button>
          </div>
        </div>
      </section>

      {/* Selectors Bar */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 bg-white p-4 rounded-2xl border border-slate-200 shadow-sm print:hidden">
        <div>
          <label className="block text-xs font-semibold text-slate-700 mb-1.5 uppercase tracking-wider">
            Select Athlete
          </label>
          <select
            value={selectedAthleteId}
            onChange={(e) => setSelectedAthleteId(e.target.value)}
            className="w-full bg-slate-50 border border-slate-300 rounded-xl px-3 py-2 text-slate-900 text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-cyan-500"
          >
            <option value="all">-- All Athletes (Bulk Print Mode) --</option>
            {athletes.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name} (BIB: {a.bib_no}) - {a.discipline_name}
              </option>
            ))}
          </select>
          {selectedAthleteId === "all" && (
            <p className="text-[11px] text-cyan-700 font-medium mt-1">
              * Bulk mode selected: Clicking "Print All Athletes" will generate continuous report cards for all {athletes.length} athletes (max 3 weeks per page, athletes separated).
            </p>
          )}
        </div>

        <div>
          <label className="block text-xs font-semibold text-slate-700 mb-1.5 uppercase tracking-wider">
            Camp Week Assessment
          </label>
          <div className="flex items-center gap-1.5 overflow-x-auto pb-1">
            {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((w) => {
              const isFilled = reportHistory.some((item) => item.week_number === w);
              return (
                <button
                  key={w}
                  type="button"
                  onClick={() => setSelectedWeek(w)}
                  disabled={selectedAthleteId === "all"}
                  className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all relative ${
                    selectedWeek === w
                      ? "bg-cyan-700 text-white shadow-sm ring-2 ring-cyan-400"
                      : isFilled
                      ? "bg-emerald-50 text-emerald-800 border border-emerald-300"
                      : "bg-slate-100 text-slate-600 hover:bg-slate-200 border border-slate-200"
                  } ${selectedAthleteId === "all" ? "opacity-50 cursor-not-allowed" : ""}`}
                >
                  W{w}
                  {isFilled && (
                    <span className="w-1.5 h-1.5 bg-emerald-600 rounded-full absolute top-1 right-1" />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Main Assessment Form & 9-Week History (Screen only) */}
      {selectedAthleteId !== "all" ? (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 print:hidden">
          {/* Weekly Evaluation Form */}
          <div className="lg:col-span-2 bg-white border border-slate-200 rounded-2xl p-6 shadow-sm space-y-6">
            <div className="flex items-center justify-between pb-4 border-b border-slate-200">
              <div>
                <h2 className="text-base font-bold text-slate-900 flex items-center gap-2">
                  <span>Week {selectedWeek} Assessment</span>
                  {reportHistory.some((h) => h.week_number === selectedWeek) && (
                    <span className="text-[10px] bg-emerald-50 text-emerald-800 border border-emerald-300 px-2.5 py-0.5 rounded-full font-bold">
                      Recorded
                    </span>
                  )}
                </h2>
                <p className="text-xs text-slate-500 mt-0.5">
                  Athlete: <span className="font-semibold text-slate-800">{selectedAthlete?.name}</span> (BIB: {selectedAthlete?.bib_no}) &bull; Father: {selectedAthlete?.father_name || "N/A"} &bull; Team: <span className="font-semibold text-cyan-800">{getTeamNameForAthlete(selectedAthlete)}</span>
                </p>
              </div>

              {selectedAthlete?.photo && (
                <div className="w-12 h-12 rounded-xl overflow-hidden border border-slate-300 shadow-sm shrink-0">
                  <img
                    src={resolveAssetUrl(selectedAthlete.photo)}
                    alt={selectedAthlete.name}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.style.display = "none";
                    }}
                  />
                </div>
              )}
            </div>

            <form onSubmit={handleSave} className="space-y-5">
              {/* Form Locked Warning Banner (Point 14) */}
              {isWeekLocked && (
                <div className="p-4 bg-amber-50 border border-amber-300 rounded-xl flex items-center gap-3 text-amber-900 text-xs font-semibold">
                  <span className="text-xl">🔒</span>
                  <div>
                    <p className="font-bold text-amber-950">Evaluation Submitted &amp; Locked</p>
                    <p className="text-[11px] text-amber-800">This week's evaluation has been finalized and locked. Contact the Admin/Secretary if modifications are needed.</p>
                  </div>
                </div>
              )}

              {/* 6 Core Parameters (Skill and Technique Separated) */}
              {[
                { label: "1. Endurance", value: endurance, setter: setEndurance, desc: "Stamina, aerobic capacity, and prolonged exertion tolerance" },
                { label: "2. Strength", value: strength, setter: setStrength, desc: "Core stability, power output, muscular force" },
                { label: "3. Agility", value: agility, setter: setAgility, desc: "Reaction time, balance, rapid direction control" },
                { label: "4. Skill", value: skill, setter: setSkill, desc: "Sport-specific maneuvers, implement handling, and execution capability" },
                { label: "5. Technique", value: technique, setter: setTechnique, desc: "Biomechanics, posture, movement efficiency, and correct form" },
                { label: "6. Discipline", value: discipline, setter: setDiscipline, desc: "Punctuality, conduct, coach receptiveness, team ethics" },
              ].map((item, idx) => (
                <div key={idx} className="p-4 bg-slate-50/70 rounded-xl border border-slate-200">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2">
                    <div>
                      <h3 className="text-sm font-bold text-slate-900">{item.label}</h3>
                      <p className="text-[11px] text-slate-500">{item.desc}</p>
                    </div>

                    <div className="flex items-center gap-1.5 flex-wrap">
                      {ratingOptions.map((opt) => (
                        <button
                          key={opt}
                          type="button"
                          disabled={isWeekLocked}
                          onClick={() => !isWeekLocked && item.setter(opt)}
                          className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all ${
                            isWeekLocked ? "opacity-75 cursor-not-allowed" : ""
                          } ${
                            item.value === opt
                              ? opt === "Excellent"
                                ? "bg-emerald-600 text-white shadow-sm"
                                : opt === "Good"
                                ? "bg-cyan-700 text-white shadow-sm"
                                : opt === "Average"
                                ? "bg-amber-500 text-white shadow-sm"
                                : "bg-rose-600 text-white shadow-sm"
                              : "bg-white text-slate-700 hover:bg-slate-100 border border-slate-300"
                          }`}
                        >
                          {opt}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              ))}

              {/* Overall Performance Rating */}
              <div className="p-4 bg-amber-50/50 rounded-xl border border-amber-200">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                  <div>
                    <h3 className="text-sm font-bold text-slate-900">Overall Week {selectedWeek} Rating</h3>
                    <p className="text-[11px] text-slate-500">Comprehensive rating summarizing overall week's performance</p>
                  </div>
                  <div className="flex items-center gap-1.5">
                    {ratingOptions.map((opt) => (
                      <button
                        key={opt}
                        type="button"
                        disabled={isWeekLocked}
                        onClick={() => !isWeekLocked && setPerformanceRating(opt)}
                        className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all ${
                          isWeekLocked ? "opacity-75 cursor-not-allowed" : ""
                        } ${
                          performanceRating === opt
                            ? "bg-slate-900 text-white shadow-sm"
                            : "bg-white text-slate-700 hover:bg-slate-100 border border-slate-300"
                        }`}
                      >
                        {opt}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* File Upload for Weekly Report Card */}
              <div className="p-4 bg-slate-50/70 rounded-xl border border-slate-200 space-y-2.5">
                <div className="flex items-center justify-between">
                  <label className="block text-xs font-bold text-slate-800 uppercase tracking-wider">
                    Weekly Report Card Document (PDF / Scan / Image)
                  </label>
                  <span className="text-[10px] text-slate-500 font-semibold">
                    Saved on Final Submit only
                  </span>
                </div>

                {/* Existing uploaded file on server */}
                {currentWeekFilePath && (
                  <div className="flex flex-wrap items-center justify-between gap-2 p-3 bg-emerald-50/80 border border-emerald-300 rounded-xl">
                    <div className="flex items-center gap-2">
                      <FaFilePdf className="text-rose-600 text-lg shrink-0" />
                      <div>
                        <div className="text-xs font-bold text-emerald-950">Active Document Attached</div>
                        <a
                          href={resolveAssetUrl(currentWeekFilePath)}
                          target="_blank"
                          rel="noreferrer"
                          className="text-[11px] font-semibold text-emerald-700 hover:underline flex items-center gap-1"
                        >
                          <FaFileAlt size={11} /> View / Download Document
                        </a>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        disabled={isWeekLocked}
                        onClick={() => fileInputRef.current?.click()}
                        className="px-3 py-1.5 bg-white text-slate-700 hover:bg-slate-100 border border-slate-300 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition shadow-sm disabled:opacity-50 cursor-pointer"
                      >
                        <FaPaperclip /> Replace File
                      </button>
                      <button
                        type="button"
                        disabled={isWeekLocked}
                        onClick={handleDeleteFile}
                        className="px-3 py-1.5 bg-rose-50 text-rose-700 hover:bg-rose-100 border border-rose-300 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition shadow-sm disabled:opacity-50 cursor-pointer"
                      >
                        <FaTrashAlt /> Delete File
                      </button>
                    </div>
                  </div>
                )}

                {/* File picker input */}
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                  <input
                    ref={fileInputRef}
                    type="file"
                    disabled={isWeekLocked}
                    accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx"
                    onChange={handleFileChange}
                    className="text-xs text-slate-600 file:mr-3 file:py-2 file:px-4 file:rounded-xl file:border-0 file:text-xs file:font-semibold file:bg-cyan-100 file:text-cyan-800 hover:file:bg-cyan-200 cursor-pointer disabled:opacity-50"
                  />
                </div>

                {/* Staged file selected for next final submit */}
                {selectedFile && (
                  <div className="flex items-center justify-between gap-2 text-xs text-amber-900 bg-amber-50 px-3.5 py-2.5 rounded-xl border border-amber-300">
                    <div className="flex items-center gap-2">
                      <FaPaperclip className="text-amber-700 text-sm shrink-0" />
                      <div>
                        <span>Selected file: <strong>{selectedFile.name}</strong> ({(selectedFile.size / 1024).toFixed(1)} KB)</span>
                        <span className="ml-2 text-[10px] font-bold uppercase tracking-wider text-amber-800 bg-amber-200/70 px-2 py-0.5 rounded-full">
                          Uploads on Final Submit
                        </span>
                      </div>
                    </div>
                    <button
                      type="button"
                      disabled={isWeekLocked}
                      onClick={() => {
                        setSelectedFile(null);
                        if (fileInputRef.current) fileInputRef.current.value = "";
                      }}
                      title="Discard staged file"
                      className="text-slate-400 hover:text-rose-600 p-1 font-semibold text-xs flex items-center gap-1"
                    >
                      <FaTimes /> Discard
                    </button>
                  </div>
                )}

                <p className="text-[11px] text-slate-500">
                  Attach drill scores, performance sheets, physical fitness cards, or coach observation notes (Max 10MB).
                  <span className="text-slate-600 font-semibold block mt-0.5">
                    Note: To prevent uploading the wrong file by mistake, selected files are only saved upon clicking "Final Submit". You can replace or delete files anytime before finalizing.
                  </span>
                </p>
              </div>

              {/* Remarks */}
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5 uppercase tracking-wider">
                  Coach's Weekly Remarks &amp; Observations
                </label>
                <textarea
                  rows={3}
                  disabled={isWeekLocked}
                  readOnly={isWeekLocked}
                  placeholder={isWeekLocked ? "Evaluation is locked." : "Enter observations on athlete progress, drills mastered, areas needing correction..."}
                  value={remarks}
                  onChange={(e) => setRemarks(e.target.value)}
                  className={`w-full bg-slate-50 border border-slate-300 rounded-xl p-3 text-xs text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 ${
                    isWeekLocked ? "opacity-75 cursor-not-allowed bg-slate-100" : ""
                  }`}
                />
              </div>

              <div className="flex flex-col sm:flex-row items-center gap-3">
                <button
                  type="button"
                  disabled={saving || isWeekLocked}
                  onClick={(e) => handleSave(e, false)}
                  className={`flex-1 py-3 px-4 rounded-xl text-xs font-bold flex items-center justify-center gap-2 border transition-all uppercase tracking-wider ${
                    isWeekLocked
                      ? "bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed"
                      : "bg-white text-slate-800 border-slate-300 hover:bg-slate-50 shadow-sm"
                  }`}
                >
                  <FaSave /> {saving ? "Saving..." : "Save Evaluation (Draft)"}
                </button>

                <button
                  type="button"
                  disabled={saving || isWeekLocked}
                  onClick={(e) => handleSave(e, true)}
                  className={`flex-1 py-3 px-4 text-white font-bold rounded-xl text-xs flex items-center justify-center gap-2 shadow-sm transition-all uppercase tracking-wider ${
                    isWeekLocked
                      ? "bg-slate-400 cursor-not-allowed opacity-80"
                      : "bg-cyan-700 hover:bg-cyan-800 disabled:opacity-50"
                  }`}
                >
                  <FaCheckCircle /> {isWeekLocked ? "Locked (Evaluation Submitted)" : `Final Submit Week ${selectedWeek} Report Card`}
                </button>
              </div>
            </form>
          </div>

          {/* 9-Week Historical Summary */}
          <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm flex flex-col justify-between">
            <div>
              <h3 className="text-base font-bold text-slate-900 flex items-center gap-2 mb-2">
                <FaHistory className="text-cyan-700" /> 9-Week Assessment History
              </h3>
              <p className="text-xs text-slate-500 mb-4">
                All weekly records are preserved historically across the 9-week coaching camp.
              </p>

              <div className="space-y-2">
                {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((w) => {
                  const record = reportHistory.find((r) => r.week_number === w);
                  return (
                    <div
                      key={w}
                      onClick={() => setSelectedWeek(w)}
                      className={`p-3 rounded-xl border flex items-center justify-between cursor-pointer transition-all ${
                        selectedWeek === w
                          ? "bg-cyan-50 border-cyan-500 shadow-sm"
                          : record
                          ? "bg-slate-50 border-slate-200 hover:bg-slate-100/80"
                          : "bg-slate-50/40 border-slate-100 opacity-60"
                      }`}
                    >
                      <div>
                        <div className="flex items-center gap-1.5">
                          <span className="font-bold text-xs text-slate-900">Week {w}</span>
                          {record?.file_path && (
                            <FaPaperclip className="text-cyan-700 text-xs" title="Document attached" />
                          )}
                        </div>
                        {record ? (
                          <p className="text-[10px] text-emerald-700 font-medium">
                            Rating: {record.performance_rating || "Good"}
                          </p>
                        ) : (
                          <p className="text-[10px] text-slate-400">Not recorded yet</p>
                        )}
                      </div>

                      <div className="text-right">
                        {record ? (
                          <span className="text-[10px] px-2.5 py-0.5 rounded-full bg-emerald-50 text-emerald-800 font-bold border border-emerald-300">
                            Completed
                          </span>
                        ) : (
                          <span className="text-[10px] text-slate-400 font-medium">Pending</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Print Signatures Notice */}
            <div className="mt-6 pt-4 border-t border-slate-200 text-center">
              <p className="text-[11px] text-slate-500 mb-2 font-medium">
                Official Report Card Verification Signatures:
              </p>
              <div className="text-[10px] text-slate-700 space-y-1 bg-slate-50 p-3 rounded-xl border border-slate-200 text-left">
                <div>&bull; {signatures?.assistant_coach_title || "Assistant Coach"}</div>
                <div>&bull; {signatures?.camp_incharge_title || "Camp In-Charge"}</div>
                <div>&bull; {signatures?.ssa_official_title || "SSA Official"}</div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        /* Bulk Mode Notification Banner */
        <div className="bg-white border border-slate-200 rounded-2xl p-8 text-center shadow-sm print:hidden">
          <div className="max-w-md mx-auto space-y-3">
            <FaPrint className="text-4xl text-cyan-700 mx-auto" />
            <h3 className="text-lg font-bold text-slate-900">All Athletes Bulk Printing Mode</h3>
            <p className="text-xs text-slate-600">
              You have selected "All Athletes". Clicking the <strong>Print All Athletes</strong> button will print complete report cards for all {athletes.length} athletes.
            </p>
            <p className="text-xs text-slate-500">
              Each athlete's report card strictly prints with maximum 3 weeks per page, and each athlete begins on a fresh page without mixing.
            </p>
            <div className="flex items-center justify-center gap-2 flex-wrap pt-1">
              <div className="inline-flex rounded-xl shadow-sm border border-slate-300 overflow-hidden">
                <button
                  type="button"
                  onClick={() => handlePrintWeeklyCards(true)}
                  className="px-3.5 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-800 text-xs font-bold inline-flex items-center gap-1.5 transition-colors border-r border-slate-300"
                  title="Print Blank Report Cards"
                >
                  <FaPrint className="text-cyan-700" /> Print Blank
                </button>
                <button
                  type="button"
                  onClick={() => handleDownloadWeeklyCards(true)}
                  className="px-3.5 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-800 text-xs font-bold inline-flex items-center gap-1.5 transition-colors"
                  title="Generate and download blank report cards"
                >
                  <FaDownload className="text-cyan-700" /> Download Blank
                </button>
              </div>

              <div className="inline-flex rounded-xl shadow-sm overflow-hidden">
                <button
                  type="button"
                  onClick={() => handlePrintWeeklyCards(false)}
                  className="px-4 py-2.5 bg-cyan-700 hover:bg-cyan-800 text-white text-xs font-bold inline-flex items-center gap-1.5 transition-colors border-r border-cyan-800"
                  title={`Print Entered Data for ${athletes.length} Athletes`}
                >
                  <FaPrint /> Print Populated ({athletes.length})
                </button>
                <button
                  type="button"
                  onClick={() => handleDownloadWeeklyCards(false)}
                  className="px-4 py-2.5 bg-cyan-800 hover:bg-cyan-900 text-white text-xs font-bold inline-flex items-center gap-1.5 transition-colors"
                  title={`Generate and download entered data for ${athletes.length} athletes`}
                >
                  <FaDownload /> Download ({athletes.length})
                </button>
              </div>

              <button
                type="button"
                onClick={() => setIndividualModalOpen(true)}
                className="px-4 py-2.5 bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl text-xs font-bold inline-flex items-center gap-2 shadow-sm transition-colors"
              >
                <FaFileAlt /> Individual Athlete Report ({athletes.length})
              </button>
            </div>
          </div>
        </div>
      )}

      {/* =========================================================
          DEDICATED PRINTABLE REPORT CARDS VIEW
          (Strictly Hidden on Screen, Rendered During Window.print())
      ========================================================= */}
      <div className="hidden print:block print-container w-full">
        {printMode.startsWith("individual_") ? (
          /* =========================================================
             FORMAT 1: INDIVIDUAL ATHLETE DAILY TRAINING EVALUATION REPORT
             (1 A4 Page per Athlete per Day - Strict Pagination)
          ========================================================= */
          (() => {
            const list = (athletes && athletes.length > 0 ? athletes : athletesToPrint.map((i) => i.athlete || i))
              .filter((ath) => {
                if (individualScope === "single") {
                  return String(ath.id) === String(selectedIndividualAthleteId);
                }
                if (individualScope === "team" && reportSelectedTeam !== "all") {
                  const t = getTeamNameForAthlete(ath);
                  return t === reportSelectedTeam;
                }
                return true;
              });

            const sortedList = [...list].sort((a, b) => {
              const teamCompare = String(getTeamNameForAthlete(a) || "General Squad")
                .localeCompare(String(getTeamNameForAthlete(b) || "General Squad"), undefined, { numeric: true, sensitivity: "base" });
              if (teamCompare !== 0) return teamCompare;
              return String(a.name || "").localeCompare(String(b.name || ""), undefined, { numeric: true, sensitivity: "base" });
            });
            const days = reportDayMode === "single_day"
              ? [{
                  dayNumber: selectedReportDay?.dayNumber || reportSingleDayNum || 1,
                  dateStr: selectedReportDay?.dateStr || (new Date().toLocaleDateString("en-GB").replace(/\//g, "-")),
                }]
              : getCampDaysList(sortedList[0]);

            const pages = [];
            days.forEach((day) => {
              sortedList.forEach((athlete) => {
                pages.push({ athlete, day });
              });
            });

            if (pages.length === 0) {
              return (
                <div className="p-8 text-center text-slate-500 bg-white">
                  No athletes found for team: <strong>{reportSelectedTeam}</strong>.
                </div>
              );
            }

            return pages.map(({ athlete, day }, pageIdx) => {
              const teamName = getTeamNameForAthlete(athlete);

              return (
                <div
                  key={`${athlete.id || 'ath'}-${day.dayNumber}-${pageIdx}`}
                  className="individual-student-page flex flex-col justify-between w-full"
                  style={{
                    background: "#ffffff",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                    height: "270mm",
                    maxHeight: "270mm",
                    boxSizing: "border-box",
                    padding: "3mm 5mm 2mm 5mm",
                    pageBreakInside: "avoid",
                    breakInside: "avoid",
                    pageBreakAfter: pageIdx === pages.length - 1 ? "auto" : "always",
                    breakAfter: pageIdx === pages.length - 1 ? "auto" : "page",
                    overflow: "hidden",
                  }}
                >
                  {/* TOP HEADER SECTION WITH 3 OFFICIAL LOGOS (EQUAL HIGHLIGHT) */}
                  <div className="flex-1 flex flex-col justify-start">
                    <div className="border-b-2 border-slate-900 pb-1 mb-1 bg-white">
                      <div className="flex items-center justify-between gap-3 mb-0.5">
                        {/* 1. SSA Logo (Left) */}
                        <div className="flex items-center justify-start shrink-0">
                          <img
                            src="/images/ssa.png"
                            alt="Samagra Shiksha"
                            style={{ height: "36px", width: "auto", objectFit: "contain" }}
                            onError={(e) => { e.currentTarget.style.display = "none"; }}
                          />
                        </div>

                        {/* 2. Equal Highlight: SAMAGRA SHIKSHA & PARA SPORTS (Center) */}
                        <div className="text-center flex-1 px-2 flex flex-col items-center justify-center">
                          <div className="text-[12px] sm:text-[13px] font-black tracking-wide text-slate-950 uppercase leading-snug">
                            SAMAGRA SHIKSHA &bull; GOVERNMENT OF ANDHRA PRADESH
                          </div>
                          <div className="flex items-center justify-center gap-1.5 my-0.5">
                            <img
                              src="/images/parasports.png"
                              alt="Para Sports"
                              style={{ height: "18px", width: "auto", objectFit: "contain" }}
                              onError={(e) => { e.currentTarget.style.display = "none"; }}
                            />
                            <span className="text-[12px] sm:text-[13px] font-black tracking-wide text-slate-900 uppercase leading-snug">
                              PARA SPORTS ASSOCIATION OF ANDHRA PRADESH
                            </span>
                          </div>
                          <p className="text-[8px] text-slate-700 font-semibold tracking-normal">
                            Recognized by Paralympic Committee of India &bull; Sports Authority of Andhra Pradesh
                          </p>
                        </div>

                        {/* 3. Government of Andhra Pradesh Emblem (Right) */}
                        <div className="flex items-center justify-end shrink-0">
                          <img
                            src="/images/ap_govt.png"
                            alt="Government of Andhra Pradesh"
                            style={{ height: "36px", width: "auto", objectFit: "contain" }}
                            onError={(e) => { e.currentTarget.style.display = "none"; }}
                          />
                        </div>
                      </div>

                      <div className="text-center mt-0.5">
                        <div className="inline-block px-3 py-0.5 bg-slate-950 text-white text-[9px] font-black uppercase tracking-wider rounded">
                          Coaching Camp &mdash; Individual Athlete Report
                        </div>
                      </div>
                    </div>

                    {/* ATHLETE & CAMP DETAILS META BOX */}
                    <div className="border border-slate-900 rounded p-1.5 mb-1.5 bg-white flex items-center justify-between gap-2.5">
                      <div className="grid grid-cols-3 gap-x-2.5 gap-y-0.5 text-[10px] text-slate-900 flex-1">
                        <div>
                          <span className="font-bold text-slate-700">Athlete Name:</span>{" "}
                          <span className="font-extrabold uppercase text-slate-950 text-[10.5px]">
                            {athlete.name}
                          </span>
                        </div>
                        <div>
                          <span className="font-bold text-slate-700">ID No:</span>{" "}
                          <span className="font-bold font-mono text-cyan-900">
                            {athlete.id_number || athlete.player?.registration_id || athlete.bib_no || "N/A"}
                          </span>
                        </div>
                        <div>
                          <span className="font-bold text-slate-700">Team Name:</span>{" "}
                          <span className="font-bold text-slate-900">
                            {teamName}
                          </span>
                        </div>
                        <div>
                          <span className="font-bold text-slate-700">District:</span>{" "}
                          <span className="font-bold text-slate-900">
                            {athlete.district || "N/A"}
                          </span>
                        </div>
                        <div>
                          <span className="font-bold text-slate-700">Sport / Discipline:</span>{" "}
                          <span className="font-bold text-slate-900">
                            {athlete.discipline_name || athlete.category || "Para Athletics"}
                          </span>
                        </div>
                        <div>
                          <span className="font-bold text-slate-700">Date:</span>{" "}
                          <span className="font-bold text-slate-950">
                            {day.dateStr} (Day {day.dayNumber})
                          </span>
                        </div>
                      </div>

                      {/* Top Right Photo */}
                      <div className="border-2 border-slate-900 rounded overflow-hidden shrink-0 bg-slate-50 flex items-center justify-center p-0.5" style={{ width: "20mm", height: "23mm" }}>
                        {athlete?.photo || athlete?.player?.photo ? (
                          <img
                            src={resolveAssetUrl(athlete.photo || athlete.player.photo)}
                            crossOrigin="anonymous"
                            alt={athlete.name}
                            className="w-full h-full object-cover rounded-sm"
                            onError={(e) => {
                              e.currentTarget.style.display = "none";
                              if (e.currentTarget.nextElementSibling) {
                                e.currentTarget.nextElementSibling.classList.remove("hidden");
                                e.currentTarget.nextElementSibling.classList.add("flex");
                              }
                            }}
                          />
                        ) : null}
                        <div
                          className={`w-full h-full flex flex-col items-center justify-center text-slate-400 bg-slate-100 ${athlete?.photo || athlete?.player?.photo ? "hidden" : "flex"}`}
                        >
                          <svg className="w-5 h-5 text-slate-400" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                          </svg>
                          <span className="text-[7px] font-bold text-slate-500 uppercase tracking-tight mt-0.5">Photo</span>
                        </div>
                      </div>
                    </div>

                    {/* TWO SIDE-BY-SIDE COLUMNS: MORNING SESSION & EVENING SESSION */}
                    <div className="grid grid-cols-2 gap-2.5 mb-1 flex-1">
                      {/* MORNING SESSION COLUMN */}
                      <div className="border-2 border-slate-900 rounded p-2 bg-white flex flex-col justify-between flex-1">
                        <div>
                          <div className="text-center pb-1 mb-1.5 border-b-2 border-slate-900 bg-slate-100 py-0.5 rounded-t">
                            <h3 className="text-[10.5px] font-black uppercase tracking-wider text-slate-950">
                              Morning Session
                            </h3>
                          </div>

                          <div className="space-y-1.5 text-[9.5px]">
                            {/* Reporting time */}
                            <div className="flex items-end gap-2 pt-0.5">
                              <span className="font-bold text-slate-900 shrink-0">Reporting time :</span>
                              <div className="border-b border-slate-400 border-dashed flex-1 h-5"></div>
                            </div>

                            {/* Warm up Exercise (5 lines) */}
                            <div className="pt-0.5">
                              <div className="font-bold text-slate-900 mb-0.5">Warm up Exercise :</div>
                              <div className="space-y-1 pt-0.5">
                                <div className="border-b border-slate-400 border-dashed h-5"></div>
                                <div className="border-b border-slate-400 border-dashed h-5"></div>
                                <div className="border-b border-slate-400 border-dashed h-5"></div>
                                <div className="border-b border-slate-400 border-dashed h-5"></div>
                                <div className="border-b border-slate-400 border-dashed h-5"></div>
                              </div>
                            </div>

                            {/* Stretches (4 lines) */}
                            <div className="pt-0.5">
                              <div className="font-bold text-slate-900 mb-0.5">Stretches :</div>
                              <div className="space-y-1 pt-0.5">
                                <div className="border-b border-slate-400 border-dashed h-5"></div>
                                <div className="border-b border-slate-400 border-dashed h-5"></div>
                                <div className="border-b border-slate-400 border-dashed h-5"></div>
                                <div className="border-b border-slate-400 border-dashed h-5"></div>
                              </div>
                            </div>

                            {/* Specific Sport (6 lines) */}
                            <div className="pt-0.5">
                              <div className="font-bold text-slate-900 mb-0.5">Specific Sport :</div>
                              <div className="space-y-1 pt-0.5">
                                <div className="border-b border-slate-400 border-dashed h-5"></div>
                                <div className="border-b border-slate-400 border-dashed h-5"></div>
                                <div className="border-b border-slate-400 border-dashed h-5"></div>
                                <div className="border-b border-slate-400 border-dashed h-5"></div>
                                <div className="border-b border-slate-400 border-dashed h-5"></div>
                                <div className="border-b border-slate-400 border-dashed h-5"></div>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="pt-1.5 border-t border-slate-300 space-y-1.5 text-[9.5px] mt-2">
                          <div className="flex items-end gap-2">
                            <span className="font-bold text-slate-900 shrink-0">Disperse time :</span>
                            <div className="border-b border-slate-400 border-dashed flex-1 h-5"></div>
                          </div>
                          <div className="flex items-end gap-2">
                            <span className="font-bold text-slate-900 shrink-0">Sign :</span>
                            <div className="border-b border-slate-400 border-dashed flex-1 h-5"></div>
                          </div>
                        </div>
                      </div>

                      {/* EVENING SESSION COLUMN */}
                      <div className="border-2 border-slate-900 rounded p-2 bg-white flex flex-col justify-between flex-1">
                        <div>
                          <div className="text-center pb-1 mb-1.5 border-b-2 border-slate-900 bg-slate-100 py-0.5 rounded-t">
                            <h3 className="text-[10.5px] font-black uppercase tracking-wider text-slate-950">
                              Evening Session
                            </h3>
                          </div>

                          <div className="space-y-1.5 text-[9.5px]">
                            {/* Reporting time */}
                            <div className="flex items-end gap-2 pt-0.5">
                              <span className="font-bold text-slate-900 shrink-0">Reporting time :</span>
                              <div className="border-b border-slate-400 border-dashed flex-1 h-5"></div>
                            </div>

                            {/* Warm up Exercise (5 lines) */}
                            <div className="pt-0.5">
                              <div className="font-bold text-slate-900 mb-0.5">Warm up Exercise :</div>
                              <div className="space-y-1 pt-0.5">
                                <div className="border-b border-slate-400 border-dashed h-5"></div>
                                <div className="border-b border-slate-400 border-dashed h-5"></div>
                                <div className="border-b border-slate-400 border-dashed h-5"></div>
                                <div className="border-b border-slate-400 border-dashed h-5"></div>
                                <div className="border-b border-slate-400 border-dashed h-5"></div>
                              </div>
                            </div>

                            {/* Stretches (4 lines) */}
                            <div className="pt-0.5">
                              <div className="font-bold text-slate-900 mb-0.5">Stretches :</div>
                              <div className="space-y-1 pt-0.5">
                                <div className="border-b border-slate-400 border-dashed h-5"></div>
                                <div className="border-b border-slate-400 border-dashed h-5"></div>
                                <div className="border-b border-slate-400 border-dashed h-5"></div>
                                <div className="border-b border-slate-400 border-dashed h-5"></div>
                              </div>
                            </div>

                            {/* Specific Sport (6 lines) */}
                            <div className="pt-0.5">
                              <div className="font-bold text-slate-900 mb-0.5">Specific Sport :</div>
                              <div className="space-y-1 pt-0.5">
                                <div className="border-b border-slate-400 border-dashed h-5"></div>
                                <div className="border-b border-slate-400 border-dashed h-5"></div>
                                <div className="border-b border-slate-400 border-dashed h-5"></div>
                                <div className="border-b border-slate-400 border-dashed h-5"></div>
                                <div className="border-b border-slate-400 border-dashed h-5"></div>
                                <div className="border-b border-slate-400 border-dashed h-5"></div>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="pt-1.5 border-t border-slate-300 space-y-1.5 text-[9.5px] mt-2">
                          <div className="flex items-end gap-2">
                            <span className="font-bold text-slate-900 shrink-0">Disperse time :</span>
                            <div className="border-b border-slate-400 border-dashed flex-1 h-5"></div>
                          </div>
                          <div className="flex items-end gap-2">
                            <span className="font-bold text-slate-900 shrink-0">Sign :</span>
                            <div className="border-b border-slate-400 border-dashed flex-1 h-5"></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* BOTTOM: OFFICIAL VERIFICATION SIGNATURES */}
                  <div
                    className="pt-1.5 border-t-2 border-slate-900 bg-white shrink-0 mt-auto"
                    style={{
                      pageBreakInside: "avoid",
                      breakInside: "avoid",
                      pageBreakBefore: "avoid",
                      breakBefore: "avoid",
                    }}
                  >
                    {(() => {
                      const coachesCount = athlete?.assigned_coaches?.length || (athlete?.mappings?.length > 1 ? 2 : 1);
                      const hasDualCoaches = coachesCount > 1;
                      const coachName = getCoachNameForAthlete(athlete);

                      return (
                        <div className={`grid ${hasDualCoaches ? "grid-cols-5" : "grid-cols-4"} gap-3 text-center text-[10px]`}>
                          <div>
                            <div className="h-12 flex items-end justify-center pb-1">
                              <span className="text-[8px] text-slate-400 font-medium tracking-wide uppercase">Sign above</span>
                            </div>
                            <div className="border-t border-slate-900 pt-1 font-bold text-slate-950 text-[10px]">
                              Athlete Signature
                            </div>
                            <div className="text-[9px] text-slate-600 font-semibold truncate">
                              {athlete.name}
                            </div>
                          </div>

                          {hasDualCoaches ? (
                            <>
                              <div>
                                <div className="h-12 flex items-end justify-center pb-1">
                                  <span className="text-[8px] text-slate-400 font-medium tracking-wide uppercase">Sign above</span>
                                </div>
                                <div className="border-t border-slate-900 pt-1 font-bold text-slate-950 text-[10px]">
                                  Assistant Coach 1
                                </div>
                                <div className="text-[9px] text-slate-600 font-semibold">
                                  Signature
                                </div>
                              </div>
                              <div>
                                <div className="h-12 flex items-end justify-center pb-1">
                                  <span className="text-[8px] text-slate-400 font-medium tracking-wide uppercase">Sign above</span>
                                </div>
                                <div className="border-t border-slate-900 pt-1 font-bold text-slate-950 text-[10px]">
                                  Assistant Coach 2
                                </div>
                                <div className="text-[9px] text-slate-600 font-semibold">
                                  Signature
                                </div>
                              </div>
                            </>
                          ) : (
                            <div>
                              <div className="h-12 flex items-end justify-center pb-1">
                                <span className="text-[8px] text-slate-400 font-medium tracking-wide uppercase">Sign above</span>
                              </div>
                              <div className="border-t border-slate-900 pt-1 font-bold text-slate-950 text-[10px]">
                                Assistant Coach
                              </div>
                              <div className="text-[9px] text-slate-600 font-semibold truncate">
                                {coachName || "Signature"}
                              </div>
                            </div>
                          )}

                          <div>
                            <div className="h-12 flex items-end justify-center pb-1">
                              <span className="text-[8px] text-slate-400 font-medium tracking-wide uppercase">Sign above</span>
                            </div>
                            <div className="border-t border-slate-900 pt-1 font-bold text-slate-950 text-[10px]">
                              Camp In-Charge
                            </div>
                            <div className="text-[9px] text-slate-600 font-semibold">
                              Signature &amp; Seal
                            </div>
                          </div>

                          <div>
                            <div className="h-12 flex items-end justify-center pb-1">
                              <span className="text-[8px] text-slate-400 font-medium tracking-wide uppercase">Sign above</span>
                            </div>
                            <div className="border-t border-slate-900 pt-1 font-bold text-slate-950 text-[10px]">
                              Official
                            </div>
                            <div className="text-[9px] text-slate-600 font-semibold">
                              Samagra Shiksha / Official
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                </div>
              );
            });
          })()
        ) : (
          /* =========================================================
             FORMAT 2: WEEKLY ASSESSMENT REPORT CARDS
          ========================================================= */
          athletesToPrint.map((item, athIdx) => {
          const athlete = item.athlete || item;
          const athleteWeeks = item.weeks || [];
          const printWeeks = getPrintWeeksForAthlete(athleteWeeks);
          const weekChunks = chunkArray(printWeeks, 9); // all 9 weeks in one page
          const coachName = getCoachNameForAthlete(athlete);
          const teamName = getTeamNameForAthlete(athlete);
          const athletePhoto = athlete?.photo ? resolveAssetUrl(athlete.photo) : null;

          return (
            <React.Fragment key={athlete.id || athIdx}>
              {weekChunks.map((chunk, chunkIdx) => {
                return (
                  <div
                    key={`${athlete.id}-chunk-${chunkIdx}`}
                    className="print-report-page"
                  >
                    {/* TOP SECTION: ASSOCIATION TITLE & ATHLETE HEADER */}
                    <div>
                      {/* Organization Header with Logos: SSA -> PARASPORTS -> Govt of AP */}
                      <div className="border-b-2 border-slate-900 pb-2 mb-3">
                        {/* Logo Row */}
                        <div className="flex items-center justify-between gap-3 mb-1">
                          {/* 1. SSA Logo (Left) */}
                          <div className="flex items-center justify-start shrink-0">
                            <img
                              src="/images/ssa.png"
                              alt="Samagra Shiksha"
                              style={{ height: "46px", width: "auto", objectFit: "contain" }}
                              onError={(e) => { e.currentTarget.style.display = "none"; }}
                            />
                          </div>

                          {/* 2. Equal Highlight: SAMAGRA SHIKSHA & PARA SPORTS (Center) */}
                          <div className="text-center flex-1 px-2 flex flex-col items-center justify-center">
                            <div className="text-[13px] sm:text-[14px] font-black tracking-wide text-slate-950 uppercase leading-snug">
                              SAMAGRA SHIKSHA &bull; GOVERNMENT OF ANDHRA PRADESH
                            </div>
                            <div className="flex items-center justify-center gap-1.5 my-0.5">
                              <img
                                src="/images/parasports.png"
                                alt="Para Sports"
                                style={{ height: "24px", width: "auto", objectFit: "contain" }}
                                onError={(e) => { e.currentTarget.style.display = "none"; }}
                              />
                              <span className="text-[13px] sm:text-[14px] font-black tracking-wide text-slate-900 uppercase leading-snug">
                                PARA SPORTS ASSOCIATION OF ANDHRA PRADESH
                              </span>
                            </div>
                            <p className="text-[8.5px] text-slate-700 font-semibold tracking-normal">
                              Recognized by Paralympic Committee of India &bull; Sports Authority of Andhra Pradesh
                            </p>
                          </div>

                          {/* 3. Government of Andhra Pradesh Emblem (Right) */}
                          <div className="flex items-center justify-end shrink-0">
                            <img
                              src="/images/ap_govt.png"
                              alt="Government of Andhra Pradesh"
                              style={{ height: "50px", width: "auto", objectFit: "contain" }}
                              onError={(e) => { e.currentTarget.style.display = "none"; }}
                            />
                          </div>
                        </div>
                        <div className="text-center mt-1">
                          <div className="inline-block px-3 py-0.5 bg-slate-950 text-white text-[10.5px] font-black uppercase tracking-wider rounded">
                            Coaching Camp &mdash; Athlete Weekly Evaluation Report Card
                          </div>
                        </div>
                      </div>

                      {/* Athlete Information Grid & Top Right Photo */}
                      <div className="border border-slate-800 rounded p-2.5 mb-3 flex items-start justify-between gap-3 bg-white">
                        {/* Athlete Details (Left) */}
                        <div className="flex-1 grid grid-cols-2 gap-x-4 gap-y-1.5 text-[11px] text-slate-900">
                          <div>
                            <span className="font-bold text-slate-700">Player Name:</span>{" "}
                            <span className="font-extrabold uppercase text-slate-950 text-xs">
                              {athlete.name}
                            </span>
                          </div>
                          <div>
                            <span className="font-bold text-slate-700">Father's Name:</span>{" "}
                            <span className="font-bold text-slate-900">
                              {athlete.father_name || "N/A"}
                            </span>
                          </div>
                          <div>
                            <span className="font-bold text-slate-700">District:</span>{" "}
                            <span className="font-bold text-slate-900">
                              {athlete.district || "N/A"}
                            </span>
                          </div>
                          <div>
                            <span className="font-bold text-slate-700">Team / Squad:</span>{" "}
                            <span className="font-bold text-cyan-900">
                              {teamName}
                            </span>
                          </div>
                          <div>
                            <span className="font-bold text-slate-700">Assistant Coach:</span>{" "}
                            <span className="font-bold text-slate-900">
                              {coachName}
                            </span>
                          </div>
                          <div>
                            <span className="font-bold text-slate-700">ID No:</span>{" "}
                            <span className="font-bold font-mono text-slate-900">
                              {athlete.id_number || athlete.player?.registration_id || athlete.bib_no || "N/A"}
                            </span>
                          </div>
                          <div>
                            <span className="font-bold text-slate-700">Discipline / Sport:</span>{" "}
                            <span className="font-bold text-slate-900">
                              {athlete.discipline_name || athlete.category || "Para Athletics"}
                            </span>
                          </div>
                          <div>
                            <span className="font-bold text-slate-700">Category:</span>{" "}
                            <span className="font-bold text-slate-900">
                              {athlete.category || "N/A"}
                            </span>
                          </div>
                        </div>

                        {/* Top Right Corner Photo */}
                        <div className="w-24 h-28 border-2 border-slate-900 rounded overflow-hidden shrink-0 bg-slate-100 flex flex-col items-center justify-center text-center">
                          {athletePhoto ? (
                            <img
                              src={athletePhoto}
                              alt={athlete.name}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.target.style.display = "none";
                                if (e.target.nextSibling) {
                                  e.target.nextSibling.style.display = "flex";
                                }
                              }}
                            />
                          ) : null}
                          <div
                            className={`w-full h-full flex flex-col items-center justify-center text-slate-400 p-1 ${
                              athletePhoto ? "hidden" : "flex"
                            }`}
                          >
                            <FaUser className="text-xl mb-1 text-slate-400" />
                            <span className="text-[9px] font-bold uppercase text-slate-500">Athlete Photo</span>
                          </div>
                        </div>
                      </div>

                      {/* Weekly Assessment Table (Max 3 weeks per page) */}
                      <div className="mb-3">
                        <div className="text-[10px] font-bold uppercase tracking-wider text-slate-800 mb-1 flex justify-between items-center">
                          <span>
                            Weekly Performance Assessment (Weeks {chunk[0].week_number} to {chunk[chunk.length - 1].week_number})
                          </span>
                          <span className="text-slate-600 font-semibold">
                            Scale: Excellent | Good | Average | Poor
                          </span>
                        </div>

                        <table className="w-full border-collapse border-2 border-slate-900 text-[11px]">
                          <thead>
                            <tr className="bg-slate-200 text-slate-950 font-black border-b-2 border-slate-900 text-center">
                              <th className="border border-slate-900 py-1.5 px-2 w-16">Week</th>
                              <th className="border border-slate-900 py-1.5 px-2">Endurance</th>
                              <th className="border border-slate-900 py-1.5 px-2">Strength</th>
                              <th className="border border-slate-900 py-1.5 px-2">Agility</th>
                              <th className="border border-slate-900 py-1.5 px-2">Skill</th>
                              <th className="border border-slate-900 py-1.5 px-2">Technique</th>
                              <th className="border border-slate-900 py-1.5 px-2">Discipline</th>
                              <th className="border border-slate-900 py-1.5 px-2 w-20">Overall Rating</th>
                              <th className="border border-slate-900 py-1.5 px-2 text-left w-52">Remarks &amp; Advice</th>
                            </tr>
                          </thead>
                          <tbody>
                            {chunk.map((wData) => {
                              const skillVal =
                                wData.skill ||
                                (wData.skill_technique
                                  ? wData.skill_technique.split("/")[0]?.trim()
                                  : "-");
                              const techniqueVal =
                                wData.technique ||
                                (wData.skill_technique
                                  ? wData.skill_technique.split("/")[1]?.trim() || "-"
                                  : "-");

                              return (
                                <tr key={wData.week_number} className="border-b border-slate-900 text-center">
                                  <td className="border border-slate-900 py-2.5 px-2 font-black bg-slate-100 text-slate-950">
                                    Week {wData.week_number}
                                  </td>
                                  <td className="border border-slate-900 py-2 px-1 font-semibold">
                                    {isBlankTemplate ? "" : (wData.endurance || "-")}
                                  </td>
                                  <td className="border border-slate-900 py-2 px-1 font-semibold">
                                    {isBlankTemplate ? "" : (wData.strength || "-")}
                                  </td>
                                  <td className="border border-slate-900 py-2 px-1 font-semibold">
                                    {isBlankTemplate ? "" : (wData.agility || "-")}
                                  </td>
                                  <td className="border border-slate-900 py-2 px-1 font-semibold">
                                    {isBlankTemplate ? "" : skillVal}
                                  </td>
                                  <td className="border border-slate-900 py-2 px-1 font-semibold">
                                    {isBlankTemplate ? "" : techniqueVal}
                                  </td>
                                  <td className="border border-slate-900 py-2 px-1 font-semibold">
                                    {isBlankTemplate ? "" : (wData.discipline || "-")}
                                  </td>
                                  <td className="border border-slate-900 py-2 px-1 font-extrabold text-slate-950 bg-slate-50">
                                    {isBlankTemplate ? "" : (wData.performance_rating || "-")}
                                  </td>
                                  <td className="border border-slate-900 py-2 px-2 text-left text-[10px] leading-tight font-medium text-slate-800">
                                    {isBlankTemplate
                                      ? ""
                                      : (wData.remarks && wData.remarks !== "-"
                                        ? wData.remarks
                                        : "Satisfactory participation and consistent effort maintained.")}
                                    {!isBlankTemplate && wData.file_path && (
                                      <div className="mt-1 text-[9px] font-bold text-slate-600">
                                        [Assessment Document Attached]
                                      </div>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* BOTTOM SECTION: NOTES & OFFICIAL VERIFICATION SIGNATURES */}
                    <div className="pt-2 border-t-2 border-slate-900">
                      <div className="flex items-center justify-between text-[10px] text-slate-600 mb-6 font-medium">
                        <span>
                          * Certified that the weekly athletic assessments recorded above reflect objective coach evaluations during camp training.
                        </span>
                        <span>
                          Page {chunkIdx + 1} of {weekChunks.length} &bull; Athlete #{athlete.bib_no || athlete.id}
                        </span>
                      </div>

                      {/* Official Verification Signatures */}
                      {(() => {
                        const coachesCount = athlete?.assigned_coaches?.length || (athlete?.mappings?.length > 1 ? 2 : 1);
                        const hasDualCoaches = coachesCount > 1;

                        return (
                          <div className={`grid ${hasDualCoaches ? "grid-cols-4" : "grid-cols-3"} gap-6 text-center text-[11px] pt-4`}>
                            {hasDualCoaches ? (
                              <>
                                <div>
                                  <div className="border-t border-slate-900 pt-1.5 font-bold text-slate-950">
                                    Assistant Coach 1
                                  </div>
                                  <div className="text-[10px] text-slate-600 font-semibold">
                                    Signature
                                  </div>
                                </div>
                                <div>
                                  <div className="border-t border-slate-900 pt-1.5 font-bold text-slate-950">
                                    Assistant Coach 2
                                  </div>
                                  <div className="text-[10px] text-slate-600 font-semibold">
                                    Signature
                                  </div>
                                </div>
                              </>
                            ) : (
                              <div>
                                <div className="border-t border-slate-900 pt-1.5 font-bold text-slate-950">
                                  Assistant Coach
                                </div>
                                <div className="text-[10px] text-slate-600 font-semibold">
                                  Signature
                                </div>
                              </div>
                            )}

                            <div>
                              <div className="border-t border-slate-900 pt-1.5 font-bold text-slate-950">
                                Camp In-Charge
                              </div>
                              <div className="text-[10px] text-slate-600 font-semibold">
                                Signature
                              </div>
                            </div>

                            <div>
                              <div className="border-t border-slate-900 pt-1.5 font-bold text-slate-950">
                                Official
                              </div>
                              <div className="text-[10px] text-slate-600 font-semibold">
                                Signature
                              </div>
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                );
              })}
            </React.Fragment>
          );
        }))}
      </div>
    </div>
  );
};

export default CoachReportCards;
